/** Job 관련 API 호출 wrapper. */

import type { AxiosProgressEvent } from "axios";

import { apiClient } from "./client";

export interface RecordEditEntry {
  /** 사람이 아닌 주체(M26 자동 검수)의 편집이면 null. */
  user_id: number | null;
  /** 사람이 아닌 편집 주체 — 자동 검수는 "auto_review". */
  actor?: string;
  timestamp: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  memo?: string | null;
  action?: string;
  amount_analysis_changed?: boolean;
}

export interface PendingExtraction {
  csv_data: Record<string, unknown>;
  amount_analysis: Record<string, unknown> | null;
  is_rejected: boolean;
  rejection_reason: string | null;
  extracted_at: string;
  triggered_by_user_id: number | null;
  success?: boolean;
  error_message?: string | null;
}

/**
 * 단일 필드의 PDF 내 위치 (M11 bbox overlay).
 *
 * - ``page``: 1-based 페이지 번호
 * - ``bbox``: PNG 픽셀 좌표 (M7-B 의 200 DPI 와 동일). [x1, y1, x2, y2]
 * - ``confidence``: 0~1 사이 fuzzy match score
 * - ``matched_text``: PaddleOCR 가 실제로 잡은 텍스트 (csv_data 의 값과 다를 수 있음 — 디버깅용)
 *
 * 단일 필드 매칭 실패 시 해당 키는 ``null``.
 * ``Record.csv_bbox`` 자체가 ``undefined`` 이면 워커가 아직 bbox 매칭을 수행하지
 * 않은 (구버전 워커가 처리한) 레코드 — frontend 는 mock 또는 미표시로 처리.
 */
export interface FieldBbox {
  page: number;
  bbox: [number, number, number, number];
  confidence: number;
  matched_text: string;
}

export interface RecordOut {
  id: string;
  csv_data: Record<string, unknown>;
  csv_bbox?: Record<string, FieldBbox | null> | null;
  is_rejected: boolean;
  rejection_reason: string | null;
  amount_analysis: Record<string, unknown> | null;
  edits: RecordEditEntry[];
  pending_extraction: PendingExtraction | null;
  // M21 #7 — 검수 상태.
  review_status: ReviewStatus;
  reviewed_at: string | null;
  ai_review: AiReview | null;
  // 2차 검수 완료(최종 사인오프) 시각.
  confirmed_at: string | null;
  // M26 자동 검수 상태 — 자동 검수 대상이 아니었던 건은 null.
  automation?: AutomationState | null;
  // 납품 CSV 의 대리인명(끝의 통화 표시를 뺀 값). 저장 값과 같으면 null.
  delivered_agent_name?: string | null;
  created_at: string;
  updated_at: string;
}

// M21 #7 — 사람 1차 검수 + 최종 AI 검토 + 2차 검수 완료 상태.
export type ReviewStatus = "none" | "human_done" | "ai_passed" | "ai_needs_review" | "confirmed";

/** M26 자동 검수 끝 상태 (backend app/automation/engine.py 의 STATUS_*). */
export type AutomationStatus =
  | "queued"
  | "running"
  | "auto_passed"
  | "auto_fixed"
  | "needs_human"
  | "human_owned"
  | "skipped"
  | "error";

export interface AutomationState {
  status: AutomationStatus;
  /** 사람 확인·건너뜀·오류의 이유 코드 (engine.py 의 REASON_*). */
  reason?: string | null;
  /** 지적 해소(다시 읽기 → 재검토) 회차. */
  rounds?: number;
  /** 자동 검수가 실제 값에 반영한 칸. */
  changed?: Record<string, { before: unknown; after: unknown }>;
  /** 사람이 고친 값을 확인한 시각·확인자 — 있으면 목록의 "확인 권장" 표시를 끈다. */
  checked_at?: string | null;
  checked_by?: { id: number; email: string | null } | null;
  locale?: string;
  queued_at?: string;
  started_at?: string;
  finished_at?: string;
}

export interface AiReview {
  verdict: "pass" | "needs_review";
  issues: string[];
  // 정상 확인된 항목(긍정 확인). needs_review 를 유발하지 않는다.
  checks?: string[];
  model: string | null;
  reviewed_at: string;
}

export interface InvoiceFileOut {
  id: string;
  original_name: string;
  size: number;
  page_count: number | null;
  status: string;
  error_message: string | null;
  record: RecordOut | null;
}

export interface JobOut {
  id: string;
  /** 소유자 user id — 관리자 감독(읽기전용) 판별용. */
  user_id: number;
  batch_name: string | null;
  // M25 — 상위 의뢰 및 zip 내 원본 폴더.
  request_id?: string | null;
  source_folder?: string | null;
  status: string;
  file_count: number;
  success_count: number;
  rejected_count: number;
  error_count: number;
  // M22 #3 — 2차 검수 완료(confirmed) 레코드 수.
  reviewed_count: number;
  // M26 — 업로드 때 '자동 검수'를 켰는지.
  auto_review?: boolean;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface JobDetailOut extends JobOut {
  files: InvoiceFileOut[];
}

export interface CreateJobOptions {
  files: File[];
  batchName?: string;
  /** M26 — 추출 후 자동 검수를 진행할지. */
  autoReview?: boolean;
  /** 자동 검수 결과 문장 언어 (화면 언어). */
  locale?: string;
  onUploadProgress?: (event: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

/** M26 자동 검수 옵션을 업로드 폼에 싣는다 (``/jobs``·``/requests`` 공용). */
export function appendAutoReview(form: FormData, autoReview?: boolean, locale?: string): void {
  if (!autoReview) return;
  form.append("auto_review", "true");
  form.append("auto_review_locale", locale || "ko");
}

export async function createJob({
  files,
  batchName,
  autoReview,
  locale,
  onUploadProgress,
  signal,
}: CreateJobOptions): Promise<JobDetailOut> {
  const form = new FormData();
  for (const f of files) {
    form.append("files", f, f.name);
  }
  if (batchName?.trim()) {
    form.append("batch_name", batchName.trim());
  }
  appendAutoReview(form, autoReview, locale);

  const res = await apiClient.post<JobDetailOut>("/jobs", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
    signal,
    // 큰 배치 업로드는 기본 60초로 부족할 수 있음 — 10분
    timeout: 10 * 60 * 1000,
  });
  return res.data;
}

export async function listJobs(): Promise<JobOut[]> {
  const res = await apiClient.get<JobOut[]>("/jobs");
  return res.data;
}

export async function getJob(jobId: string): Promise<JobDetailOut> {
  const res = await apiClient.get<JobDetailOut>(`/jobs/${jobId}`);
  return res.data;
}

/** Job 배치명(제목) 수정. 빈 값이면 익명 처리(null). */
export async function updateJob(jobId: string, batchName: string | null): Promise<JobOut> {
  const res = await apiClient.patch<JobOut>(`/jobs/${jobId}`, {
    batch_name: batchName,
  });
  return res.data;
}

export type CsvType = "delivery" | "review" | "rejected_delivery" | "rejected_review";

export async function downloadCsv(jobId: string, type: CsvType): Promise<Blob> {
  const res = await apiClient.get<Blob>(`/jobs/${jobId}/csv`, {
    params: { type },
    responseType: "blob",
  });
  return res.data;
}

/** content-disposition 헤더에서 RFC 5987 filename* 우선 파싱. */
function filenameFromDisposition(disposition: string | undefined, fallback: string): string {
  if (!disposition) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* fallthrough */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? fallback;
}

/** 작업 원본 PDF 일괄 다운로드 (ZIP). (blob, 서버가 정한 파일명) 반환. */
export async function downloadJobPdfZip(jobId: string): Promise<{ blob: Blob; filename: string }> {
  const res = await apiClient.get<Blob>(`/jobs/${jobId}/pdf-zip`, {
    responseType: "blob",
  });
  const disposition = res.headers?.["content-disposition"] as string | undefined;
  return { blob: res.data, filename: filenameFromDisposition(disposition, "job_pdfs.zip") };
}

export interface CsvPreview {
  headers: string[];
  rows: string[][];
  count: number;
  /** rows[i] 에 대응하는 InvoiceFile.id — 행 클릭 → 케이스 편집 모달용. */
  file_ids?: string[];
}

/** CSV 출력 미리보기(JSON) — 다운로드 없이 표로 렌더용. */
export async function getCsvPreview(jobId: string, type: CsvType): Promise<CsvPreview> {
  const res = await apiClient.get<CsvPreview>(`/jobs/${jobId}/csv/preview`, {
    params: { type },
  });
  return res.data;
}

/**
 * Job 을 영구 삭제한다.
 *
 * 백엔드 정책 (참조: ``backend/app/services/jobs.py::delete_job``):
 *   - 본인 Job 또는 admin (``role==='admin'``) 만 가능.
 *   - 그 외 비-owner 는 ``404`` 로 통일 (소유 정보 노출 회피).
 *   - PENDING / PROCESSING / COMPLETED / FAILED 모든 상태에서 삭제 가능.
 *   - 성공 시 ``204 No Content`` — body 없음.
 *
 * DB 의 cascade 가 ``InvoiceFile`` / ``Record`` 까지 정리하고, 디스크의
 * ``UPLOAD_DIR / {job_id}`` 디렉토리도 best-effort 로 함께 제거된다.
 */
export async function deleteJob(jobId: string): Promise<void> {
  await apiClient.delete(`/jobs/${jobId}`);
}

/**
 * 진행 중 Job 을 중지한다(우아한 중지).
 *
 * 백엔드(``POST /jobs/{id}/cancel``)가 상태를 ``cancelling`` 으로 전이 →
 * 대기열 파일은 즉시 skip, 진행 중이던 1~2건은 끝난 뒤 ``cancelled`` 로 마감된다.
 * owner 또는 admin 만 가능(그 외 404). 진행 중이 아니면 409.
 */
export async function cancelJob(jobId: string): Promise<JobOut> {
  const res = await apiClient.post<JobOut>(`/jobs/${jobId}/cancel`);
  return res.data;
}

/**
 * 중지/실패/멈춘 Job 을 재시도한다(재-dispatch).
 *
 * 백엔드(``POST /jobs/{id}/retry``)가 상태를 ``pending`` 으로 되돌리고 다시
 * 큐잉한다. 이미 처리된 파일은 idempotency 로 skip, 미처리/실패 파일만 재처리.
 * owner 또는 admin 만 가능(그 외 404). completed/pending 은 409.
 */
export async function retryJob(jobId: string): Promise<JobOut> {
  const res = await apiClient.post<JobOut>(`/jobs/${jobId}/retry`);
  return res.data;
}
